import * as React from 'react';
import { Container } from 'typescript-ioc/es5';
import { BetterComponent } from '../../components/better-component/better-component';
import { ModuleLoadingScreen } from '../../components/module-loading-screen/module-loading-screen';
import { Route, Switch } from '../../components/router';
import { GetDomainCode } from './get-domain-code/get-domain-code';
import { PromptEditor, PromptList } from '../prompts';
import { NotificationDetails } from '../notifications';
import { Dashboard } from '../dashboard';
import Sandbox from '../sandbox/sandbox';
import { TestDetails } from '../notifications/notification-details/notification-test-details';
import { Redirect } from 'react-router';
import { CampaignList } from '../campaigns/campaign-list/campaign-list';
import { CreateCampaign } from '../campaigns/create-campaign/create-campaign';
import { CampaignDetails } from '../campaigns/campaign-details/campaign-details';
import { EditCampaign } from '../campaigns/edit-campaign/edit-campaign';
import { AppService } from '../../services';
import { AccessDenied403 } from '../../components/403/403';
import { Domain } from '../post-auth/domain';
import { snakeCase } from 'change-case';
import { SendNotification2 } from '../notifications/send-notification/send-notification-2';
import { EditNotification2 } from '../notifications/edit-notification/edit-notification-2';
import Notifications from '../post-auth/notifications';
import { Insights } from '../reporting_v2/insights';
import Segments from '../post-auth/segments';
import Segment from '../post-auth/segment';
import { SegmentDetails } from '../segments/segment-details/segment-details';
import { AbilityAction } from '../../enums/ability-action.enum';
import { SubjectEntity } from '../../enums/ability-entity.enum';
import { ICaslSubject } from '../../stores/app-ability';
import { DomainDto } from '../../dtos/domain';
import { AppState } from '../../stores/app';
import { observer } from 'mobx-react';

interface IProps {}

interface IState {}

@observer
export class DomainFeature extends BetterComponent<IProps, IState> {

    private appState: AppState;
    private appService: AppService;

    public constructor(props: IProps) {
        super(props);

        this.appState = Container.get(AppState);
        this.appService = Container.get(AppService);
    }

    public render() {
        const _ = this.appState.abilityStore.acs.state;

        const pathClassname = snakeCase(this.injectedProps.location?.pathname ?? '')
            .replace(/_?[\d]+_?/g, '_');

        return (
            <div className={`module notifications ${pathClassname}`}>
                <ModuleLoadingScreen />

                <Switch>
                    <Route
                        path={`${this.matchedPath}/__sandbox__`}
                        exact={true}
                        component={Sandbox}
                    />
                    <Route
                        path={`${this.matchedPath}/dashboard`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.READ, this.appState.currentDomain!, Dashboard)}
                    />
                    <Route
                        path={`${this.matchedPath}/insights`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.READ, this.appState.currentDomain!, Insights)}
                    />

                    <Route
                        path={`${this.matchedPath}`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.UPDATE, this.appState.currentDomain!, Domain)}
                    />
                    <Route
                        path={`${this.matchedPath}/code`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.READ, this.appState.currentDomain!, GetDomainCode)}
                    />

                    <Route
                        path={`${this.matchedPath}/notifications`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.READ, SubjectEntity.NOTIFICATION, Notifications)}
                    />
                    <Redirect
                        from={`${this.matchedPath}/notifications/:notificationId([0-9]+)`}
                        to={`${this.matchedPath}/notifications/:notificationId([0-9]+)/summary`}
                        exact={true}
                    />
                    <Route
                        path={`${this.matchedPath}/notifications/:notificationId([0-9]+)/:tab([\\w]+)`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.READ, SubjectEntity.NOTIFICATION, NotificationDetails)}
                    />
                    <Redirect
                        from={`${this.matchedPath}/notifications/test/:testId([0-9]+)`}
                        to={`${this.matchedPath}/notifications/test/:testId([0-9]+)/summary`}
                        exact={true}
                    />
                    <Route
                        path={`${this.matchedPath}/notifications/test/:testId([0-9]+)/:tab([\\w]+)`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.READ, SubjectEntity.NOTIFICATION_TEST, TestDetails)}
                    />
                    <Route
                        path={`${this.matchedPath}/notifications/new`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.CREATE, SubjectEntity.NOTIFICATION, SendNotification2)}
                    />
                    <Route
                        path={`${this.matchedPath}/notifications/edit/:notificationId([0-9]+)`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.UPDATE, SubjectEntity.NOTIFICATION, EditNotification2)}
                    />

                    <Route
                        path={`${this.matchedPath}/campaigns`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.READ, SubjectEntity.CAMPAIGN, CampaignList)}
                    />

                    <Route
                        path={`${this.matchedPath}/campaigns/new`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.CREATE, SubjectEntity.CAMPAIGN, CreateCampaign)}
                    />

                    <Route
                        path={`${this.matchedPath}/campaigns/:campaignId([0-9]+)/:tab(configuration|builder)`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.UPDATE, SubjectEntity.CAMPAIGN, EditCampaign)}
                    />

                    <Redirect
                        from={`${this.matchedPath}/campaigns/:campaignId([0-9]+)`}
                        to={`${this.matchedPath}/campaigns/:campaignId([0-9]+)/summary`}
                        exact={true}
                    />
                    <Route
                        path={`${this.matchedPath}/campaigns/:campaignId([0-9]+)/:tab([\\w]+)`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.READ, SubjectEntity.CAMPAIGN, CampaignDetails)}
                    />

                    <Route
                        path={`${this.matchedPath}/prompts`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.READ, SubjectEntity.PROMPT, PromptList)}
                    />
                    <Route
                        path={`${this.matchedPath}/prompts/new`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.CREATE, SubjectEntity.PROMPT, PromptEditor)}
                    />
                    <Route
                        path={`${this.matchedPath}/prompts/:promptId([0-9]+)`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.UPDATE, SubjectEntity.PROMPT, PromptEditor)}
                    />

                    <Route
                        path={`${this.matchedPath}/segments/new`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.CREATE, SubjectEntity.SEGMENT, Segment)}
                    />
                    <Route
                        path={[
                            `${this.matchedPath}/segments`,
                            `${this.matchedPath}/segments/compare`,
                        ]}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.READ, SubjectEntity.SEGMENT, Segments)}
                    />
                    <Route
                        path={`${this.matchedPath}/segments/:segmentId([0-9]+)`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.UPDATE, SubjectEntity.SEGMENT, Segment)}
                    />
                    <Route
                        path={`${this.matchedPath}/segments/:segmentId([0-9]+)/:tab([\\w]+)`}
                        exact={true}
                        component={this.buildComponentCheck(AbilityAction.READ, SubjectEntity.SEGMENT, SegmentDetails)}
                    />
                </Switch>
            </div>
        );
    }

    protected buildComponentCheck<T extends SubjectEntity>(
        action: AbilityAction,
        check: T | ICaslSubject<T> | DomainDto,
        component: any,
        altComponent?: any,
    ) {
        altComponent = altComponent ?? AccessDenied403;

        const isDomainCheck = check instanceof DomainDto || check === SubjectEntity.DOMAIN;
        const entity = isDomainCheck ? check : this.appState.abilityStore.getDomainOwnedIdentityFor(check as T);

        return this.appState.abilityStore.can(action, entity as ICaslSubject<T> | DomainDto) ? component : altComponent;
    }

}
